import { Component, Input, OnInit, Optional, Self, WritableSignal, computed, input, signal } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { TimeService, utils } from '@bayclubs/utils';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment-timezone';
import { DateConsts } from './date-consts';

@Component({
    selector: 'app-date-picker',
    templateUrl: './date-picker.component.html',
    styleUrls: ['./date-picker.component.scss'],
    standalone: false
})
export class DatePickerComponent implements OnInit, ControlValueAccessor {
    constructor(
        private translateService: TranslateService,
        private timeService: TimeService,
        @Optional() @Self() public ngControl: NgControl
    ) {
        if (this.ngControl != null) {
            this.ngControl.valueAccessor = this;
        }
    }

    model = new DatePickerModel();

    max = input<utils.BcDate>();
    min = input<utils.BcDate>();

    readonly _max = computed(() => {
        const v = this.max();
        const m = this.min();
        if (!v || (m && this.timeService.toMoment(m) > this.timeService.toMoment(v))) {
            return this.timeService.toBcDate(new Date(DateConsts.defaultMaxYear, 1, 1));
        }
        return v;
    });

    readonly _min = computed(() => {
        const v = this.min();
        const m = this.max();
        if (!v || (m && this.timeService.toMoment(v) > this.timeService.toMoment(m))) {
            return this.timeService.toBcDate(new Date(DateConsts.defaultMinYear, 1, 1));
        }
        return v;
    });

    readonly maxMoment = computed(() => this.timeService.toMoment(this._max()));
    readonly minMoment = computed(() => this.timeService.toMoment(this._min()));

    private _value?: utils.BcDate;
    isMonthPickerOpened = false;
    isDayPickerOpened = false;
    isYearPickerOpened = false;

    @Input() disabled = false;

    get value(): utils.BcDate {
        return this._value;
    }

    set value(value: utils.BcDate) {
        if (!this.disabled) {
            this._value = value;
            this.onChange(this.value);
        }
    }

    ngOnInit() {
        this.setStartDate();
    }

    setStartDate(): void {
        const today = moment();
        const startOfTodayYear = this.timeService.startOfYear(today);
        const minM = this.minMoment();
        const maxM = this.maxMoment();
        const date =
            this.min() && minM > startOfTodayYear
                ? this.timeService.startOfMonth(this.timeService.addMonths(minM, 1))
                : this.max() && startOfTodayYear > maxM
                    ? this.timeService.startOfMonth(this.timeService.addMonths(maxM, -1))
                    : startOfTodayYear;
        this.model.date.set(date);
    }

    onChange = (value: utils.BcDate) => { };
    onTouched = () => { };

    getMonthTitle(): string {
        const m = this.model.month();
        return m !== null ? moment.months(m) : this.translateService.instant('Date.Month');
    }

    openMonthPicker(): void {
        this.isMonthPickerOpened = !this.isMonthPickerOpened;
    }

    closeMonthPicker(event: MouseEvent): void {
        if (this.isDisabledCellClicked(event)) {
            return;
        }

        this.isMonthPickerOpened = false;
        this.writeValue(null);
    }

    getDayTitle(): string {
        const d = this.model.day();
        return d !== null ? `${ d }` : this.translateService.instant('Date.Day');
    }

    openDayPicker(): void {
        this.isDayPickerOpened = !this.isDayPickerOpened;
    }

    closeDayPicker(event: any): void {
        if (this.isDisabledCellClicked(event) || this.isDayPickerClickedOutsideOfCell(event)) {
            return;
        }

        this.isDayPickerOpened = false;
        this.writeValue(null);
    }

    isDayPickerClickedOutsideOfCell(event: any): boolean {
        return event.target && event.target.id === 'day-picker-row';
    }

    getYearTitle(): string {
        const y = this.model.year();
        return y !== null ? `${ y }` : this.translateService.instant('Date.Year');
    }

    getMinYear(): number {
        return this.minMoment().year();
    }

    getMaxYear(): number {
        return this.maxMoment().year();
    }

    openYearPicker(): void {
        this.isYearPickerOpened = !this.isYearPickerOpened;
    }

    closeYearPicker(event: any): void {
        if (this.isDisabledCellClicked(event) || this.isLeftOrRightYearColumnClicked(event)) {
            return;
        }
        this.isYearPickerOpened = false;
        this.writeValue(null);
    }

    isLeftOrRightYearColumnClicked(event: any): boolean {
        return (
            (event.target.lastChild && event.target.lastChild.id && event.target.lastChild.id === 'right-year-column') ||
            (event.target.id && event.target.id === 'right-year-button') ||
            (event.target.id && event.target.id === 'right-year-column') ||
            (event.target.lastChild && event.target.lastChild.id && event.target.lastChild.id === 'left-year-column') ||
            (event.target.id && event.target.id === 'left-year-button') ||
            (event.target.id && event.target.id === 'left-year-column')
        );
    }

    isDisabledCellClicked(event: any): boolean {
        return event.target.classList && event.target.classList.contains('disabled-cell');
    }

    writeValue(value: utils.BcDate): void {
        if (this.value && (value && !value.value)) {
            this.resetModel();
            this.value = null;
            return;
        }

        value == null || value.value == null ? this.setDateFromPicker() : this.setDateAtStart(value);
    }

    resetModel(): void {
        this.model.day.set(null);
        this.model.month.set(null);
        this.model.year.set(null);
        this.model.date.set(null);
        this.setStartDate();
    }

    setDateAtStart(value: utils.BcDate): void {
        this.value = value;
        const momentDate = this.timeService.toMoment(value);
        this.model.month.set(momentDate.month());
        this.model.day.set(momentDate.date());
        this.model.year.set(momentDate.year());
        this.model.date.set(momentDate);
    }

    setDateFromPicker(): void {
        const d = this.model.day();
        const m = this.model.month();
        const y = this.model.year();
        if (d != null && m != null && y != null) {
            this.value = this.timeService.toBcDate(new Date(y, m, d));
        } else {
            this.value = null;
        }
    }

    registerOnChange(fn: any) {
        this.onChange = fn;
    }

    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}

export interface DatePickerInputModel {
    date: utils.BcDate;
    resetData: boolean;
}

export class DatePickerModel {
    readonly month: WritableSignal<number | null> = signal(null);
    readonly day: WritableSignal<number | null> = signal(null);
    readonly year: WritableSignal<number | null> = signal(null);
    readonly date: WritableSignal<moment.Moment | null> = signal(null);
}
